<template>
  <section
    class="f24-bank-coordinates"
    :class="{ 'bank-expanded': expanded, 'bank-help-open': helpOpen }"
  >
    <div class="bank-toolbar codconto-table-dashboard">
      <div class="bank-toolbar-context">
        <div class="bank-toolbar-title-row">
          <span class="bank-toolbar-label">Conti di addebito</span>
          <span class="bank-toolbar-count">{{ candidates.length }}</span>
          <span class="bank-toolbar-separator" aria-hidden="true"></span>
          <button
            type="button"
            class="bank-help-trigger"
            :aria-expanded="helpOpen ? 'true' : 'false'"
            aria-controls="bank-help-content"
            @click="helpOpen = !helpOpen"
          >
            <i class="fa-solid fa-circle-info" aria-hidden="true"></i>
            Come gestire i conti
          </button>
        </div>
        <span
          v-if="bankDescriptionSummary"
          class="bank-toolbar-description"
          :title="bankDescriptionSummary"
        >
          <span>Descrizione:</span> {{ bankDescriptionSummary }}
        </span>
      </div>
      <div class="bank-toolbar-actions">
        <button
          type="button"
          class="codconto-dashboard-button bank-icon-button bank-expand-button"
          :disabled="disabled"
          :title="expanded ? 'Comprimi tabella' : 'Espandi tabella'"
          :aria-label="expanded ? 'Comprimi tabella' : 'Espandi tabella'"
          :aria-pressed="expanded ? 'true' : 'false'"
          @click="$emit('toggle-expand')"
        >
          <i
            :class="expanded ? 'fa-solid fa-compress' : 'fa-solid fa-expand'"
          ></i>
        </button>
        <button
          type="button"
          class="codconto-dashboard-button"
          :class="{ active: editing }"
          :disabled="disabled"
          @click="editing = !editing"
        >
          <i class="fa-regular fa-pen-to-square"></i>
          {{ editing ? "Chiudi modifica" : "Modifica" }}
        </button>
        <button
          type="button"
          class="codconto-dashboard-button codconto-dashboard-save"
          :disabled="disabled || !dirty"
          @click="save"
        >
          <i class="fa-solid fa-floppy-disk"></i> Salva
        </button>
        <button
          type="button"
          class="codconto-dashboard-button bank-icon-button"
          :disabled="disabled || !history.length"
          title="Annulla ultima modifica"
          @click="undo"
        >
          <i class="fa-solid fa-rotate-left"></i>
        </button>
        <button
          type="button"
          class="codconto-dashboard-button codconto-dashboard-reset"
          :disabled="disabled || !dirty"
          @click="reset"
        >
          Annulla
        </button>
      </div>
    </div>
    <div v-show="helpOpen" id="bank-help-content" class="bank-help-panel">
      Se non ci sono conti salvati, inserisci il codice conto nella cella.
      Altrimenti scegli un conto dal menu: "Nuovo conto +" per creare un'altra
      associazione. Modificare il conto selezionato aggiorna quello esistente.
    </div>
    <p v-if="errors.length" class="bank-errors" role="alert">
      {{ errors.join(" ") }}
    </p>
    <div class="bank-table-scroll">
      <table>
        <colgroup>
          <col class="bank-col-index" />
          <col class="bank-col-client" />
          <col class="bank-col-fiscal" />
          <col class="bank-col-code" />
          <col class="bank-col-code" />
          <col class="bank-col-iban" />
          <col class="bank-col-date" />
          <col class="bank-col-account" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col" aria-label="Numero riga"></th>
            <th>Denominazione cliente</th>
            <th>Codice Fiscale</th>
            <th>ABI</th>
            <th>CAB</th>
            <th>IBAN</th>
            <th>Data pagamento</th>
            <th>Codice conto</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(group, index) in candidates" :key="index">
            <td>{{ index + 1 }}</td>
            <td>{{ displayRow(index)["Denominazione Cliente"] }}</td>
            <td>{{ coordinates(index)[0] }}</td>
            <td>
              <input
                v-if="coordinateEditable(index, 1)"
                :value="displayBankCode(index, 1)"
                aria-label="Codice ABI"
                inputmode="numeric"
                maxlength="5"
                :disabled="disabled"
                @input="updateCode(index, 1, $event.target.value)"
              /><span v-else>{{ displayBankCode(index, 1) }}</span>
            </td>
            <td>
              <input
                v-if="coordinateEditable(index, 2)"
                :value="displayBankCode(index, 2)"
                aria-label="Codice CAB"
                inputmode="numeric"
                maxlength="5"
                :disabled="disabled"
                @input="updateCode(index, 2, $event.target.value)"
              /><span v-else>{{ displayBankCode(index, 2) }}</span>
            </td>
            <td class="bank-iban-cell">
              <IbanMaskedInput
                v-if="ibanEditable(index)"
                class="bank-iban-input"
                :value="ibanValue(index)"
                :invalid="Boolean(ibanIssue(index))"
                :title="
                  [ibanValue(index), ibanIssue(index)]
                    .filter(Boolean)
                    .join(' — ')
                "
                :disabled="disabled"
                @focus="focusedIbanIndex = index"
                @blur="handleIbanBlur(index)"
                @input="updateIban(index, $event)"
              />
              <span
                v-else
                class="bank-iban-readonly"
                :class="{ 'bank-iban-invalid': ibanIssue(index) }"
                :title="
                  [ibanValue(index), ibanIssue(index)]
                    .filter(Boolean)
                    .join(' — ')
                "
                >{{ formattedIbanValue(index) }}</span
              >
            </td>
            <td>{{ numericPaymentDate(displayRow(index).Data) }}</td>
            <td class="bank-account-cell">
              <div
                class="bank-account-entry"
                :class="accountValidationClasses(index)"
              >
                <input
                  v-if="showInlineAccountInput(index)"
                  :value="account(index)"
                  aria-label="Codice conto"
                  :aria-invalid="!isAccountValid(index)"
                  :title="isAccountValid(index) ? '' : accountFormatHint()"
                  :placeholder="
                    isNew(index)
                      ? 'Inserisci cod. conto'
                      : 'Inserisci codice conto'
                  "
                  :disabled="disabled"
                  @input="
                    update(
                      index,
                      accountKey(index),
                      formatBankAccount($event.target.value, software)
                    )
                  "
                />
                <span
                  v-else-if="!hasSavedAssociations(index)"
                  class="bank-account-value"
                  >â€”</span
                >
                <span
                  v-if="hasSavedAssociations(index)"
                  class="bank-account-picker"
                  :class="{
                    'bank-account-picker--full': !showInlineAccountInput(index),
                  }"
                >
                  <select
                    :key="selectedOriginalId(index) || 'unselected'"
                    :value="selectedOriginalId(index)"
                    :disabled="disabled"
                    :aria-label="'Seleziona conto del pagamento ' + (index + 1)"
                    title="Scegli un conto o aggiungine uno nuovo"
                    @change="selectAccount(index, $event.target.value)"
                  >
                    <option value="" disabled>Seleziona un conto</option>
                    <option
                      v-for="candidate in availableAssociations(index)"
                      :key="candidate['ID Associazione']"
                      :value="candidate['ID Associazione']"
                    >
                      {{ candidateLabel(candidate) }}
                    </option>
                    <option value="__new_association__">Nuovo conto +</option>
                  </select>
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-if="saved" class="bank-saved" role="status">
      Modifiche confermate. Le associazioni saranno salvate con la conversione.
    </p>
  </section>
</template>

<script>
import {
  bankAccountKey,
  bankSoftwareKey,
  formatBankAccount,
  validBankAccount,
  bankErrors,
  formatItalianIban,
  newBankId,
  normalizeIban,
  numericPaymentDate,
} from "@/utils/f24Banking";
import IbanMaskedInput from "@/components/IbanMaskedInput.vue";
const clone = (value) => JSON.parse(JSON.stringify(value));
export default {
  name: "F24BankCoordinatesTable",
  components: { IbanMaskedInput },
  props: {
    value: { type: Array, required: true },
    candidates: { type: Array, required: true },
    clients: { type: Array, default: () => [] },
    software: { type: String, required: true },
    reservedAccount: { type: String, default: "" },
    disabled: Boolean,
    expanded: Boolean,
  },
  data() {
    return {
      editing: false,
      helpOpen: false,
      baseline: clone(this.value),
      history: [],
      errors: [],
      saved: false,
      pendingIbans: {},
      focusedIbanIndex: null,
    };
  },
  computed: {
    dirty() {
      return (
        JSON.stringify(this.value) !== JSON.stringify(this.baseline) ||
        Object.keys(this.pendingIbans).length > 0
      );
    },
    bankDescriptionSummary() {
      const descriptions = this.candidates
        .map((_, index) => this.description(index))
        .filter(Boolean);
      return [...new Set(descriptions)].join(", ");
    },
  },
  methods: {
    formatBankAccount,
    normalizeIban,
    numericPaymentDate,
    ibanValue(index) {
      return Object.prototype.hasOwnProperty.call(this.pendingIbans, index)
        ? this.pendingIbans[index]
        : this.displayRow(index).IBAN || "";
    },
    formattedIbanValue(index) {
      return formatItalianIban(this.ibanValue(index));
    },
    ibanEditable(index) {
      return (
        this.editing ||
        this.focusedIbanIndex === index ||
        !this.ibanValue(index)
      );
    },
    ibanIssue(index) {
      const [cf = "", abi = "", cab = ""] = this.coordinates(index);
      return (
        bankErrors(cf, abi, cab, this.ibanValue(index)).find(
          (error) => error.startsWith("IBAN") || error.startsWith("ABI e CAB")
        ) || ""
      );
    },
    setIbanValue(index, iban) {
      if (this.selectedRow(index)) {
        this.update(index, "IBAN", iban);
        return;
      }
      if (iban) this.$set(this.pendingIbans, index, iban);
      else this.$delete(this.pendingIbans, index);
      this.errors = [];
      this.saved = false;
    },
    updateIban(index, value) {
      const normalizedValue = normalizeIban(value).slice(0, 27);
      this.setIbanValue(index, normalizedValue);
    },
    handleIbanBlur(index) {
      if (this.focusedIbanIndex === index) this.focusedIbanIndex = null;
    },
    selectedRow(index) {
      return this.value[index]?.[0] || null;
    },
    displayRow(index) {
      return this.selectedRow(index) || this.candidates[index][0];
    },
    coordinates(index) {
      return String(
        this.displayRow(index)["Codice Fiscale-ABI-CAB"] || ""
      ).split("-");
    },
    displayBankCode(index, part) {
      const code = this.coordinates(index)[part] || "";
      return code === "00000" ? "" : code;
    },
    accountKey(index) {
      return bankAccountKey(
        this.displayRow(index),
        this.software,
        this.clients
      );
    },
    descriptionKey(index) {
      return `Descrizione Cod. Conto - ${bankSoftwareKey(
        this.displayRow(index),
        this.software,
        this.clients
      )}`;
    },
    account(index) {
      const value = this.selectedRow(index)?.[this.accountKey(index)];
      return !value ||
        [
          "VUOTO",
          "N.A.",
          "NESSUNA CORRELAZIONE",
          this.reservedAccount,
        ].includes(value)
        ? ""
        : String(value);
    },
    showInlineAccountInput(index) {
      return Boolean(this.selectedRow(index));
    },
    isAccountValid(index) {
      return validBankAccount(
        this.account(index),
        this.software,
        this.reservedAccount
      );
    },
    accountValidationClasses(index) {
      const selected = this.selectedRow(index);
      if (!selected) return {};
      const baseline = this.baseline[index]?.[0];
      const key = this.accountKey(index);
      const changed =
        selected["ID Associazione"] !== baseline?.["ID Associazione"] ||
        String(selected[key] || "") !== String(baseline?.[key] || "");
      const valid = this.isAccountValid(index);
      return {
        "bank-account-invalid": !valid,
        "bank-account-valid": valid && changed,
      };
    },
    accountFormatHint() {
      return {
        TEAMSYSTEM: "Formato richiesto: 00/00/000",
        DATEV: "Formato richiesto: da 5 a 11 cifre",
        SEAC: "Formato richiesto: 6 cifre",
        SISTEMI: "Formato richiesto: 00.00.00 o 00.00.00.000",
        ZUCCHETTI: "Formato richiesto: almeno 5 cifre",
      }[this.software];
    },
    description(index) {
      return this.displayRow(index)[this.descriptionKey(index)] || "BANCA c/c";
    },
    isNew(index) {
      const id = this.selectedRow(index)?.["ID Associazione"];
      return (
        Boolean(id) &&
        !this.availableAssociations(index).some(
          (row) => row["ID Associazione"] === id
        )
      );
    },
    availableAssociations(index) {
      const candidates = this.candidates[index] || [];
      const saved = this.baseline[index]?.[0];
      const savedId = saved?.["ID Associazione"];
      if (
        !savedId ||
        candidates.some((row) => row["ID Associazione"] === savedId)
      ) {
        return candidates;
      }
      const account = saved[bankAccountKey(saved, this.software, this.clients)];
      return validBankAccount(account, this.software, this.reservedAccount)
        ? [...candidates, saved]
        : candidates;
    },
    hasSavedAssociations(index) {
      return this.availableAssociations(index).some((row) =>
        Boolean(row["ID Associazione"])
      );
    },
    selectedOriginalId(index) {
      return this.isNew(index)
        ? ""
        : this.selectedRow(index)?.["ID Associazione"] || "";
    },
    selectAccount(index, id) {
      if (id === "__new_association__") this.add(index);
      else this.select(index, id);
    },
    coordinateEditable(index, part) {
      const code = this.coordinates(index)[part];
      return (
        Boolean(this.selectedRow(index)) &&
        (this.editing || !code || code === "00000")
      );
    },
    candidateLabel(row) {
      const selected = this.value
        .flat()
        .find((item) => item["ID Associazione"] === row["ID Associazione"]);
      const key = bankAccountKey(row, this.software, this.clients);
      const account = selected?.[key] ?? row[key];
      const iban = selected ? selected.IBAN : row.IBAN;
      return `${account || "Conto da completare"}${
        iban ? ` \u00b7 ${iban}` : ""
      }`;
    },
    emit(next) {
      this.history.push(clone(this.value));
      this.errors = [];
      this.saved = false;
      this.$emit("input", next);
    },
    update(index, key, value) {
      const next = clone(this.value);
      const id = next[index][0]["ID Associazione"];
      // The same archived association can serve more than one payment.
      next.forEach((group) => {
        if (group[0]?.["ID Associazione"] === id) group[0][key] = value;
      });
      this.emit(next);
    },
    updateCode(index, part, value) {
      const codes = this.coordinates(index);
      codes[part] = value.replace(/\D/g, "").slice(0, 5);
      this.update(index, "Codice Fiscale-ABI-CAB", codes.join("-"));
    },
    select(index, id) {
      const candidate = this.availableAssociations(index).find(
        (row) => row["ID Associazione"] === id
      );
      if (!candidate) return;
      const next = clone(this.value);
      const row = clone(candidate);
      const existing = next
        .flat()
        .find((item) => item["ID Associazione"] === id);
      if (existing) {
        Object.keys(existing)
          .filter(
            (key) =>
              key === "Codice Fiscale-ABI-CAB" ||
              (key.startsWith("Cod. Conto AVERE") && existing[key] != null)
          )
          .forEach((key) => {
            row[key] = existing[key];
          });
        // A different payment may still have the same association without an
        // IBAN. Keep an edited value, otherwise restore the archive's IBAN.
        row.IBAN =
          normalizeIban(existing.IBAN) || normalizeIban(candidate.IBAN) || null;
      }
      if (Object.prototype.hasOwnProperty.call(this.pendingIbans, index)) {
        row.IBAN = this.pendingIbans[index];
        this.$delete(this.pendingIbans, index);
      }
      next[index] = [row];
      this.emit(next);
    },
    add(index) {
      const row = clone(this.displayRow(index));
      const used = new Set(
        [
          ...this.candidates.flat(),
          ...this.baseline.flat(),
          ...this.value.flat(),
        ].map((item) => item["ID Associazione"])
      );
      row["ID Associazione"] = newBankId(used);
      Object.keys(row)
        .filter((key) => key.startsWith("Cod. Conto AVERE"))
        .forEach((key) => {
          row[key] = null;
        });
      if (Object.prototype.hasOwnProperty.call(this.pendingIbans, index)) {
        row.IBAN = this.pendingIbans[index];
        this.$delete(this.pendingIbans, index);
      }
      const next = clone(this.value);
      next[index] = [row];
      this.emit(next);
    },
    editedFieldErrors() {
      const errors = [];
      this.value.forEach((group, index) => {
        const row = group[0];
        const previous = this.baseline[index]?.[0];
        const previousIban = normalizeIban(
          previous?.IBAN || this.candidates[index]?.[0]?.IBAN
        );
        const ibanChanged =
          Object.prototype.hasOwnProperty.call(this.pendingIbans, index) ||
          normalizeIban(this.ibanValue(index)) !== previousIban;
        if (ibanChanged && this.ibanIssue(index)) {
          errors.push(`Pagamento ${index + 1}: ${this.ibanIssue(index)}`);
        }
        if (!row) {
          if (ibanChanged && !this.ibanIssue(index)) {
            errors.push(
              `Pagamento ${
                index + 1
              }: seleziona un conto prima di salvare l'IBAN.`
            );
          }
          return;
        }
        const [, previousAbi = "", previousCab = ""] = String(
          previous?.["Codice Fiscale-ABI-CAB"] || ""
        ).split("-");
        const [, abi = "", cab = ""] = this.coordinates(index);
        if (previous && abi !== previousAbi && !/^\d{5}$/.test(abi)) {
          errors.push(
            `Pagamento ${index + 1}: inserisci un ABI di cinque cifre.`
          );
        } else if (previous && abi !== previousAbi && abi === "00000") {
          errors.push(`Pagamento ${index + 1}: inserisci un ABI valido.`);
        }
        if (previous && cab !== previousCab && !/^\d{5}$/.test(cab)) {
          errors.push(
            `Pagamento ${index + 1}: inserisci un CAB di cinque cifre.`
          );
        } else if (previous && cab !== previousCab && cab === "00000") {
          errors.push(`Pagamento ${index + 1}: inserisci un CAB valido.`);
        }
        if (
          previous &&
          !ibanChanged &&
          (abi !== previousAbi || cab !== previousCab)
        ) {
          const mismatch = this.ibanIssue(index);
          if (mismatch) errors.push(`Pagamento ${index + 1}: ${mismatch}`);
        }
        const key = this.accountKey(index);
        const accountChanged =
          !previous ||
          row["ID Associazione"] !== previous["ID Associazione"] ||
          String(row[key] || "") !== String(previous[key] || "");
        if (accountChanged && !this.isAccountValid(index)) {
          errors.push(
            `Pagamento ${index + 1}: inserisci un codice conto valido.`
          );
        }
      });
      return errors;
    },
    save() {
      this.errors = this.editedFieldErrors();
      if (this.errors.length) return;
      this.baseline = clone(this.value);
      this.history = [];
      this.editing = false;
      this.saved = true;
    },
    undo() {
      if (this.history.length) this.$emit("input", this.history.pop());
      this.errors = [];
      this.saved = false;
    },
    reset() {
      this.$emit("input", clone(this.baseline));
      this.history = [];
      this.errors = [];
      this.saved = false;
      this.editing = false;
      this.pendingIbans = {};
      this.focusedIbanIndex = null;
    },
  },
};
</script>

<style scoped>
.f24-bank-coordinates {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  min-width: 0;
}
.bank-toolbar {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 0.7rem;
}
.f24-bank-coordinates .bank-toolbar {
  margin-bottom: 0.25rem;
  border-radius: 10px;
}
.bank-toolbar-context,
.bank-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.bank-toolbar-context {
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  color: #47596b;
  font-size: 0.78rem;
  font-weight: 700;
}
.bank-toolbar-title-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}
.bank-toolbar-description {
  display: block;
  max-width: min(36rem, 100%);
  color: #78858e;
  font-size: 0.72rem;
  font-style: italic;
  font-weight: 500;
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bank-toolbar-description span {
  color: #68757f;
  font-weight: 600;
}
.bank-toolbar-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.35rem;
  height: 1.35rem;
  padding: 0 0.25rem;
  border-radius: 999px;
  background: #e8eef3;
  color: #2d546c;
  font-size: 0.68rem;
}
.bank-toolbar-separator {
  flex: 0 0 auto;
  height: 1rem;
  margin: 0 0.3rem;
  border-left: 1px solid #cbd6df;
}
.bank-help-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.15rem;
  border: 0;
  background: transparent;
  color: #48647a;
  cursor: pointer;
  font: inherit;
  font-weight: 500;
  white-space: nowrap;
}
.bank-help-trigger:hover,
.bank-help-trigger:focus-visible {
  color: #1c5e98;
  text-decoration: underline;
}
.bank-toolbar-actions .codconto-dashboard-button {
  cursor: pointer;
}
.bank-toolbar-actions .bank-icon-button {
  width: 31px;
  padding: 0;
}
.bank-toolbar-actions .bank-expand-button {
  order: 99;
}
.bank-help-panel {
  box-sizing: border-box;
  flex: 0 0 auto;
  width: calc(100% - 1rem);
  margin: 0 auto;
  padding: 0.5rem 0.7rem;
  border: 1px solid #dce5eb;
  border-radius: 10px 10px 0 0;
  background: #f9fbfc;
  color: #596c7a;
  font-size: 0.75rem;
  line-height: 1.4;
  text-align: left;
}
.bank-table-scroll {
  box-sizing: border-box;
  flex: 0 1 auto;
  width: calc(100% - 1rem);
  min-height: 0;
  max-height: min(27rem, calc(100vh - 23rem));
  margin: 0 auto;
  overflow: auto;
  border: 1px solid #dce5eb;
  border-radius: 10px;
  background: #fff;
}
.bank-help-open .bank-table-scroll {
  border-top: 0;
  border-radius: 0 0 10px 10px;
}
.bank-expanded .bank-table-scroll {
  width: 100%;
  max-height: calc(100vh - 13rem);
}
.bank-expanded .bank-toolbar,
.bank-expanded .bank-help-panel,
.bank-expanded .bank-errors,
.bank-expanded .bank-saved {
  width: 100%;
}
table {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  color: #233b50;
  font-size: 0.79rem;
}
.bank-col-index {
  width: 4%;
}
.bank-col-client {
  width: 13.5%;
}
.bank-col-fiscal {
  width: 15.5%;
}
.bank-col-code {
  width: 6.7%;
}
.bank-col-iban {
  width: 25%;
}
.bank-col-date {
  width: 9.3%;
}
.bank-col-account {
  width: 19.3%;
}
th,
td {
  padding: 0.56rem 0.55rem;
  border: 0;
  border-bottom: 1px solid #e8edf1;
  border-right: 1px solid #e8eff3;
  text-align: left;
  vertical-align: middle;
  overflow-wrap: anywhere;
}
th:last-child,
td:last-child {
  border-right: 0;
}
th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f1f5f7;
  color: #425970;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.2;
}
td:first-child {
  color: #7a8b9a;
  text-align: center;
  white-space: nowrap;
  overflow-wrap: normal;
}
td:nth-child(3) {
  white-space: nowrap;
  overflow-wrap: normal;
}
td:nth-child(4),
td:nth-child(5),
th:nth-child(8) {
  white-space: nowrap;
  overflow-wrap: normal;
}
tbody tr:nth-child(even) td {
  background: #f9f9f9;
}
tbody tr:last-child td {
  border-bottom: 0;
}
input,
select {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  height: 30px;
  padding: 0 0.45rem;
  border: 1px solid #cbd6df;
  border-radius: 6px;
  background: #fff;
  color: #233b50;
  font: inherit;
}
input:focus-visible,
select:focus-visible,
button:focus-visible {
  outline: 2px solid #2367ad;
  outline-offset: 2px;
}
td:nth-child(4) input,
td:nth-child(5) input {
  padding: 0 0.25rem;
}
.bank-iban-cell input,
.bank-iban-readonly {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bank-iban-input {
  --iban-input-height: 30px;
}
.bank-iban-cell span.bank-iban-invalid {
  border-radius: 4px;
  background: #fff2f2;
  color: #a62121;
}
.bank-account-entry {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-height: 30px;
}
.bank-account-entry input,
.bank-account-value {
  flex: 1 1 auto;
  min-width: 0;
}
.bank-account-value {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 0.45rem;
  border: 1px solid transparent;
  border-radius: 6px;
}
.bank-account-picker {
  position: relative;
  display: inline-flex;
  flex: 0 0 23px;
  align-items: center;
  justify-content: center;
  width: 23px;
  height: 30px;
  border: 1px solid #cbd6df;
  border-radius: 6px;
  background: #fff;
  color: #2367ad;
}
.bank-account-picker:hover {
  border-color: #2367ad;
  background: #edf5fc;
}
.bank-account-picker:focus-within {
  outline: 2px solid #2367ad;
  outline-offset: 2px;
}
.bank-account-picker::after {
  width: 5px;
  height: 5px;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  content: "";
  pointer-events: none;
  transform: translateY(-2px) rotate(45deg);
}
.bank-account-picker select {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: transparent;
  font-size: 0;
  appearance: none;
  cursor: pointer;
}
.bank-account-picker select:focus-visible {
  outline: none;
}
.bank-account-picker select:disabled {
  cursor: not-allowed;
}
.bank-account-picker select option {
  color: #233b50;
  font-size: 0.79rem;
}
.bank-account-picker--full {
  flex: 1 1 auto;
  width: 100%;
  border: 0;
  background: transparent;
}
.bank-account-picker--full:hover {
  border: 0;
  background: transparent;
}
.bank-account-picker--full:focus-within {
  outline: none;
}
.bank-account-picker--full::after {
  display: none;
}
.bank-account-picker--full select {
  position: static;
  width: 100%;
  padding: 0 0.45rem;
  border: 1px solid #cbd6df;
  background: #fff;
  color: #233b50;
  font-size: inherit;
  appearance: auto;
}
.bank-account-picker--full select:focus-visible {
  outline: 2px solid #2367ad;
}
.bank-account-invalid input,
.bank-account-invalid .bank-account-value {
  border-color: #d44a52;
  background: #fff2f2;
}
.bank-account-valid input,
.bank-account-valid .bank-account-value {
  border-color: #46a66a;
  background: #eef9f1;
}
.bank-account-invalid input:focus-visible {
  outline-color: #d44a52;
}
.bank-account-valid input:focus-visible {
  outline-color: #46a66a;
}
.bank-errors,
.bank-saved {
  box-sizing: border-box;
  flex: 0 0 auto;
  width: calc(100% - 1rem);
  margin: 0.4rem auto;
  padding: 0.5rem 0.7rem;
  border-radius: 6px;
  font-size: 0.76rem;
}
.bank-errors {
  max-height: 3rem;
  overflow: auto;
  color: #a62121;
  background: #fff1f1;
}
.bank-saved {
  color: #226541;
  background: #edf8f0;
}
@media (max-width: 980px) {
  .bank-toolbar {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .bank-toolbar-actions {
    flex-wrap: wrap;
  }
}
</style>
